import {
  Button,
  CustomFilter,
  EllipsisSpan,
  Form,
  Paging,
  PopConfirm,
  Select,
  Table,
  Tooltip,
  useBreadcrumbRoutes,
  usePaging,
  useUpdate,
  Wrapper,
} from '@maxtropy/components';
import React, { useState } from 'react';
import styles from './index.module.scss';
import { Space } from 'antd';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import dayjs from 'dayjs';
import CreateProductionConfigModal from './CreateProductionConfigModal';
import {
  apiV2ProductionCostDeleteConfigPost,
  apiV2ProductionCostGetConfigListPost,
  apiV2ProductionCostGetEnergyUnitListPost,
  apiV2ProductionCostGetProductionListPost,
  V2ProductionCostGetConfigListPostRequest,
  V2ProductionCostGetConfigListPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { isNil } from 'lodash-es';

export interface SearchParams extends Omit<V2ProductionCostGetConfigListPostRequest, 'page' | 'size'> {}
export type configListItemProps = Exclude<V2ProductionCostGetConfigListPostResponse['list'], undefined>[number];

const columns = [
  {
    title: '用能单元',
    dataIndex: 'energyUnitName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '选择产品',
    dataIndex: 'productionName',
    ellipsis: { showTitle: true },
    render: (v: string, record: configListItemProps) => (
      <EllipsisSpan value={!isNil(v) ? v + '-' + record.productionUnitName : '--'} />
    ),
  },
  {
    title: '产量来源',
    dataIndex: 'deviceName',
    ellipsis: { showTitle: true },
    render: (v: boolean) => <EllipsisSpan value={v} />,
  },
  {
    title: (
      <>
        转化系数
        <Tooltip title="例如转换系数为10，则展示产品=选择产品产量/10">
          <InfoCircleOutlined style={{ color: 'var(--warning-color)', paddingLeft: 5 }} />
        </Tooltip>
      </>
    ),
    dataIndex: 'transferRate',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={!isNil(v) ? v.toFixed(2) : '--'} />,
  },
  {
    title: '展示产品',
    dataIndex: 'showProductionName',
    ellipsis: { showTitle: true },
    render: (v: boolean, record: configListItemProps) => (
      <EllipsisSpan value={!isNil(v) ? v + '-' + record.showProductionUnitName : '--'} />
    ),
  },
  {
    title: '开始生效日期',
    dataIndex: 'startTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '--'} />,
  },
  {
    title: '结束生效日期',
    dataIndex: 'endTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '--'} />,
  },
  {
    title: '最后操作人',
    dataIndex: 'updateUser',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '最后操作时间',
    dataIndex: 'updateTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '--'} />,
  },
];

const ProductionConfig = () => {
  // const hasProductionConfigAdd = useHasPermission(PermissionsType.B_BALANCESERVERRULEADD); // 新增产量配置
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [searchForm] = Form.useForm(); // 搜索的表单
  const [searchParams, setSearchParams] = useState<SearchParams>(); // 搜索参数
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo; // 分页的一些配置
  const [updateState, updateFn] = useUpdate(); // 更新
  const [openCreateModal, setOpenCreateModal] = useState(false); // 新建配置弹窗
  const [recordId, setRecordId] = useState<number>(); // 每行数据的id

  // 获取配置过的用能单元列表
  const { data: unitlist } = useRequest(() =>
    apiV2ProductionCostGetEnergyUnitListPost({}).then(res => {
      return res.list;
    })
  );

  // 获取所有产品列表
  const { data: productionlist } = useRequest(() =>
    apiV2ProductionCostGetProductionListPost({
      flag: false,
    }).then(res => {
      return res.list;
    })
  );

  // 请求分页
  const { data: list, loading } = useRequest(
    () =>
      apiV2ProductionCostGetConfigListPost({
        page: pageOffset,
        size: pageSize,
        ...searchParams,
      }).then(res => {
        setTotalCount(res.total!);
        return res.list;
      }),
    {
      refreshDeps: [pageOffset, pageSize, searchParams, updateState],
    }
  );

  // 搜索表单提交
  const onFinish = (val: SearchParams) => {
    const params = {
      energyUnitId: val.energyUnitId,
      showProductionId: val.showProductionId,
    };
    setSearchParams(params);
    setPageOffset(1);
  };

  // 搜索重置
  const onReset = () => {
    const params = {
      energyUnitId: undefined,
      showProductionId: undefined,
    };
    setSearchParams(params);
    setPageOffset(1);
  };

  // 删除
  const onDelete = (id?: number) => {
    apiV2ProductionCostDeleteConfigPost({
      id,
    }).then(_ => {
      updateFn();
    });
  };

  // 表格操作栏
  const buildColumns = [
    ...columns,
    {
      title: '操作',
      fixed: 'right' as 'right',
      width: 280,
      render: (record: configListItemProps) => (
        <Space size={16}>
          {true && (
            <Button
              type="link"
              onClick={() => {
                setOpenCreateModal(true);
                setRecordId(record.id);
              }}
            >
              编辑
            </Button>
          )}
          {true && (
            <PopConfirm placement="top" title="是否删除该产量？删除后无法恢复。" onConfirm={() => onDelete(record.id)}>
              <Button type="link">删除</Button>
            </PopConfirm>
          )}
        </Space>
      ),
    },
  ];

  const routes = [{ name: '产量配置' }];

  return (
    <Wrapper
      routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]}
      className={styles.content}
      filters={
        <CustomFilter form={searchForm} onFinish={(val: SearchParams) => onFinish(val)} onReset={onReset} colSpan={6}>
          <Form.Item name="energyUnitId" label="用能单元">
            <Select
              placeholder="请选择"
              showSearch
              options={(unitlist ?? []).map(i => ({
                label: i.name,
                value: i.id,
              }))}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="showProductionId" label="展示产品">
            <Select
              placeholder="请选择"
              showSearch
              options={(productionlist ?? []).map(i => ({
                label: i.name,
                value: i.id,
              }))}
              optionFilterProp="label"
            />
          </Form.Item>
        </CustomFilter>
      }
    >
      <Space size={8} style={{ marginBottom: 10 }}>
        {true && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setOpenCreateModal(true);
            }}
          >
            新增产量配置
          </Button>
        )}
      </Space>

      <Table
        rowKey="id"
        sticky
        scroll={{ x: 1300 }}
        loading={loading}
        columns={buildColumns}
        dataSource={list}
        pagination={false}
      />
      <Paging pagingInfo={pagingInfo} />

      <CreateProductionConfigModal
        openCreateModal={openCreateModal}
        id={recordId}
        onConfirm={() => {
          setOpenCreateModal(false);
          setRecordId(undefined);
          updateFn();
        }}
        onCancel={() => {
          setOpenCreateModal(false);
          setRecordId(undefined);
        }}
      />
    </Wrapper>
  );
};

export default ProductionConfig;
